import axios from '../api';

const API_ROOT = (process.env.REACT_APP_URL || 'http://localhost:5100').replace(/\/$/, '');

/**
 * Ubah URL file lama/public menjadi path protected /files/...
 * Contoh:
 *  http://localhost:5100/files/images/a.jpg → http://localhost:5100/files/images/a.jpg
 *  /images/a.jpg → .../files/images/a.jpg
 *  images/a.jpg → .../files/images/a.jpg
 */
export function toProtectedFileUrl(input) {
  if (!input) return '';
  let value = String(input).trim();
  if (!value || value.startsWith('blob:') || value.startsWith('data:')) return value;

  // absolute API URL
  if (value.startsWith(API_ROOT)) {
    value = value.slice(API_ROOT.length);
  }
  // bare absolute other host — keep as-is (external)
  if (/^https?:\/\//i.test(value) && !value.startsWith(API_ROOT)) {
    return value;
  }

  value = value.replace(/\\/g, '/').replace(/^\/+/, '');

  // already protected
  if (value.startsWith('files/')) {
    return `${API_ROOT}/${value}`;
  }

  // bare BA filenames should resolve to the BA preview route
  if (value.startsWith('BA_Verifikasi_') || value.startsWith('BA_Signed_')) {
    return `${API_ROOT}/ba/${value}`;
  }

  // legacy public paths
  if (value.startsWith('uploads/')) {
    return `${API_ROOT}/files/${value.slice('uploads/'.length)}`;
  }
  if (value.startsWith('images/')) {
    return `${API_ROOT}/files/${value}`;
  }
  if (value.startsWith('rab/file/')) {
    // dedicated RAB route remains
    return `${API_ROOT}/${value}`;
  }
  if (value.startsWith('sk-penetapan/file/')) {
    // dedicated SK Penetapan route remains
    return `${API_ROOT}/${value}`;
  }
  if (value.startsWith('files/sk-penetapan/')) {
    // file SK hasil auto-attach disalin ke uploads/rab-progress-administrasi (nama SK_)
    const name = value.slice('files/sk-penetapan/'.length);
    return `${API_ROOT}/files/rab-progress-administrasi/${name}`;
  }
  if (value.startsWith('ba/')) {
    return `${API_ROOT}/${value}`;
  }

  // bare filename / folder/file
  return `${API_ROOT}/files/${value}`;
}

const blobCache = new Map();
const BLOB_CACHE_MAX = 200; // max 200 blob URL di memory (~mencegah memory leak)

function pruneBlobCache() {
  if (blobCache.size <= BLOB_CACHE_MAX) return;
  // Hapus entry tertua (Map menjaga insertion order)
  const toDelete = blobCache.size - BLOB_CACHE_MAX;
  let deleted = 0;
  for (const [key, val] of blobCache.entries()) {
    if (deleted >= toDelete) break;
    try { if (val && val.startsWith('blob:')) URL.revokeObjectURL(val); } catch {}
    blobCache.delete(key);
    deleted++;
  }
}

/**
 * Fetch file dengan cookie session, kembalikan blob: URL untuk <img>/<object>.
 */
export async function fetchAuthedBlobUrl(inputUrl) {
  if (!inputUrl) return '';
  if (String(inputUrl).startsWith('blob:') || String(inputUrl).startsWith('data:')) {
    return inputUrl;
  }

  const protectedUrl = toProtectedFileUrl(inputUrl);
  if (blobCache.has(protectedUrl)) {
    return blobCache.get(protectedUrl);
  }

  // external non-API: use as-is
  if (/^https?:\/\//i.test(protectedUrl) && !protectedUrl.startsWith(API_ROOT)) {
    return protectedUrl;
  }

  const response = await axios.get(protectedUrl, {
    responseType: 'blob',
    withCredentials: true,
  });
  const blobUrl = URL.createObjectURL(response.data);
  pruneBlobCache();
  blobCache.set(protectedUrl, blobUrl);
  return blobUrl;
}

export function revokeBlobUrl(blobUrl) {
  if (blobUrl && String(blobUrl).startsWith('blob:')) {
    try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
    for (const [key, val] of blobCache.entries()) {
      if (val === blobUrl) blobCache.delete(key);
    }
  }
}
